'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { FileVersion } from '@/types/chat';
interface UseFileVersionsOptions {
  sessionId: string;
  isConnected: boolean;
}
export function useFileVersions({
  sessionId,
  isConnected
}: UseFileVersionsOptions) {
  const [files, setFiles] = useState<FileVersion[]>([]);
  const [allFiles, setAllFiles] = useState<FileVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const refresh = useCallback(async () => {
    if (!isConnected || !sessionId) return;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [collapsed, expanded] = await Promise.all([apiClient.listFileVersions('default-study', sessionId), apiClient.listFileVersions('default-study', sessionId, {
        all: true
      })]);
      setFiles(collapsed.versions ?? []);
      setAllFiles(expanded.versions ?? []);
    } catch (err) {
      // A 404 legitimately means "this session has no saved files" → empty.
      // Any other error (timeout, 500, network) is a real load failure: keep
      // whatever we already had and flag a retry instead of wiping to empty —
      // wiping reads as data loss (live report, 2026-06-17, relogin timeout).
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('404')) {
        setFiles([]);
        setAllFiles([]);
      } else {
        console.warn('[FileVersions] Failed to fetch:', err);
        setLoadFailed(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isConnected]);

  // Fetch on mount and when sessionId changes
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for save events to auto-refresh
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('crucible:files-changed', handler);
    return () => window.removeEventListener('crucible:files-changed', handler);
  }, [refresh]);
  return useMemo(() => ({
    files,
    allFiles,
    isLoading,
    loadFailed,
    refresh
  }), [files, allFiles, isLoading, loadFailed, refresh]);
}